import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieBanner } from "@/components/CookieBanner";
import { ScrollToTop } from "@/components/ScrollToTop";
import { RouteFallback } from "@/components/RouteFallback";
import { BootCleanup } from "@/components/BootCleanup";
import { HelpWidget } from "@/components/HelpWidget";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { usePageTracking } from "@/hooks/usePageTracking";
import { quickUpdateImport } from "@/lib/routePrefetch";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded routes - wszystko poza Index/NotFound, żeby zmniejszyć główny bundle.
const Admin = lazy(() => import("./pages/Admin"));
const SubmitUpdate = lazy(() => import("./pages/SubmitUpdate"));
const ShelterPage = lazy(() => import("./pages/ShelterPage"));
const ClaimToken = lazy(() => import("./pages/auth/ClaimToken"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const PanelLayout = lazy(() => import("./pages/panel/PanelLayout"));
const PanelDashboard = lazy(() => import("./pages/panel/Dashboard"));
const PanelEditProfile = lazy(() => import("./pages/panel/EditProfile"));
const PanelEditNeeds = lazy(() => import("./pages/panel/EditNeeds"));
const PanelEditAdoptions = lazy(() => import("./pages/panel/EditAdoptions"));
const PanelEditFundraiser = lazy(() => import("./pages/panel/EditFundraiser"));
const PanelStatistics = lazy(() => import("./pages/panel/Statistics"));
const PanelGraphics = lazy(() => import("./pages/panel/Graphics"));
const PanelClaimShelter = lazy(() => import("./pages/panel/ClaimShelter"));
const PanelTeam = lazy(() => import("./pages/panel/Team"));
const PanelAcceptTeamInvite = lazy(() => import("./pages/panel/AcceptTeamInvite"));
const PanelAccountSettings = lazy(() => import("./pages/panel/AccountSettings"));
const PanelHistory = lazy(() => import("./pages/panel/History"));
const PanelSetInitialPassword = lazy(() => import("./pages/panel/SetInitialPassword"));

const QuickUpdate = lazy(quickUpdateImport);
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const ForShelters = lazy(() => import("./pages/ForShelters"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const AddShelterChoice = lazy(() => import("./pages/AddShelterChoice"));
const UpdateShelterChoice = lazy(() => import("./pages/UpdateShelterChoice"));
const ClaimAccess = lazy(() => import("./pages/ClaimAccess"));
const About = lazy(() => import("./pages/About"));
const Articles = lazy(() => import("./pages/Articles"));
const Article = lazy(() => import("./pages/Article"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const SheltersHub = lazy(() => import("./pages/SheltersHub"));

const PageTracker = () => {
  usePageTracking();
  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <GoogleAnalytics />
        <Suspense fallback={<RouteFallback />}>
          <BootCleanup />
          <MaintenanceGate>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/index" element={<Index />} />
            <Route path="/index.html" element={<Index />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/dodaj-schronisko" element={<AddShelterChoice />} />
            <Route path="/zglos" element={<SubmitUpdate />} />
            <Route path="/zglos/:slug" element={<SubmitUpdate />} />
            <Route path="/aktualizuj-schronisko" element={<UpdateShelterChoice />} />
            <Route path="/aktualizuj" element={<QuickUpdate />} />
            <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
            <Route path="/regulamin" element={<Terms />} />
            <Route path="/dla-schronisk" element={<ForShelters />} />
            <Route path="/o-projekcie" element={<About />} />
            <Route path="/artykuly" element={<Articles />} />
            <Route path="/artykuly/:slug" element={<Article />} />
            <Route path="/schronisko/:slug" element={<ShelterPage />} />
            <Route path="/schroniska" element={<SheltersHub />} />
            <Route path="/schroniska/wojewodztwo/:region" element={<LocationPage kind="region" />} />
            <Route path="/schroniska/:city" element={<LocationPage kind="city" />} />
            <Route path="/logowanie" element={<Login />} />
            <Route path="/rejestracja" element={<Register />} />
            <Route path="/zapomnialem-hasla" element={<ForgotPassword />} />
            <Route path="/reset-hasla" element={<ResetPassword />} />
            <Route path="/przejmij/:token" element={<ClaimToken />} />
            <Route path="/przejmij/:slug/:token" element={<ClaimToken />} />
            <Route path="/uzyskaj-dostep/:shelterId" element={<ClaimAccess />} />
            <Route path="/panel" element={<PanelLayout />}>
              <Route index element={<PanelDashboard />} />
              <Route path="profil" element={<PanelDashboard />} />
              <Route path="profil/:slug" element={<PanelEditProfile />} />
              <Route path="potrzeby" element={<PanelDashboard />} />
              <Route path="potrzeby/:slug" element={<PanelEditNeeds />} />
              <Route path="adopcje" element={<PanelDashboard />} />
              <Route path="adopcje/:slug" element={<PanelEditAdoptions />} />
              <Route path="zbiorka" element={<PanelDashboard />} />
              <Route path="zbiorka/:slug" element={<PanelEditFundraiser />} />
              <Route path="statystyki" element={<PanelDashboard />} />
              <Route path="statystyki/:slug" element={<PanelStatistics />} />
              <Route path="grafiki" element={<PanelDashboard />} />
              <Route path="grafiki/:slug" element={<PanelGraphics />} />
              <Route path="historia" element={<PanelDashboard />} />
              <Route path="historia/:slug" element={<PanelHistory />} />
              <Route path="przejmij" element={<PanelClaimShelter />} />
              <Route path="zespol" element={<PanelTeam />} />
              <Route path="zespol/dolacz" element={<PanelAcceptTeamInvite />} />
              <Route path="konto" element={<PanelAccountSettings />} />
              <Route path="ustaw-haslo" element={<PanelSetInitialPassword />} />
            </Route>
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </MaintenanceGate>
        </Suspense>
          <CookieBanner />
          <HelpWidget />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
